import { formatDate } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { map, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { ConfirmEndOfOutboundModalComponent } from 'src/app/components/position-operations/end-outbound-scan-gle/confirm-end-of-outbound-modal/confirm-end-of-outbound-modal.component';

import { Dialog } from '@angular/cdk/dialog';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { EndOutboundScanGleService } from 'src/app/core/services/position-operations/end-outbound-scan-gle/end-outbound-scan-gle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import { IMAGE_SRC, NO_RESULTS } from '../../shared-constants';
import {
  CONST,
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  EndOutboundScan,
  ExportColumnText,
} from './end-outbound-scan-table.constant';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-end-outbound-scan-table',
  templateUrl: './end-outbound-scan-table.component.html',
  styleUrls: ['./end-outbound-scan-table.component.scss'],
  standalone: false,
})
export class EndOutboundScanTableComponent implements OnInit {
  private readonly _endOutboundScanGleService = inject(
    EndOutboundScanGleService,
  );
  private readonly userService = inject(UserService);
  private commonUtils = inject(CommonUtils);
  private dataUtils = inject(DataUtils);
  private dialog = inject(MatDialog);

  readonly SRC = IMAGE_SRC;
  readonly CONST = { ...CONST };
  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;

  // Create column search objects
  createAdminTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  movementData = this.createAdminTableColumn(ColumnIds.MOVEMENT);
  departDate = this.createAdminTableColumn(ColumnIds.DEPART_DATE);
  destroy = inject(DestroyRef);
  tableData: unknown[] = [];
  dataSource = new MatTableDataSource<EndOutboundScan>();
  maxDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 1,
  );
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );

  /**
   * Setting up filter for seach fields
   *
   * @memberof EndOutboundScanTableComponent
   */
  ngOnInit(): void {
    this.getStoppableMovements();
    this.subscribes();
    this.dataSource.filterPredicate = this.createFilter();
  }

  /**
   * used to subscribes async data from service
   *
   * @memberof EndOutboundScanTableComponent
   */
  subscribes() {
    this.dataUtils.endOutboundScan$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((resp) => {
        if (resp) {
          this.deleteOutboundScan(resp.index);
        }
      });

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(() => {
          this.resetState();
        }),
      )
      .subscribe();
  }

  resetState() {
    this.tableData = [];
    this.clearAllFilters();
    this.getStoppableMovements();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof EndOutboundScanTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [this.movementData, this.departDate],
      this.dataSource,
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof EndOutboundScanTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.tableData as EndOutboundScan[],
      [this.movementData.formControl, this.departDate.formControl],
    );
  }

  /**
   * Export table data in excel file format
   *
   * @memberof EndOutboundScanTableComponent
   */
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((item) => {
      const { movement, departDate } = item;
      return { movement, departDate };
    });
    this.commonUtils.exportExcel(
      Object.values(ExportColumnText),
      EXCEL_FILE_NAME,
      filteredData,
    );
  }

  /**
   * Used to open confirm end scan success modal
   *
   *
   * @memberof OutboundScanTableComponent
   */
  action(endOutboundScan: EndOutboundScan, index: number) {
    this.dialog.open(ConfirmEndOfOutboundModalComponent, {
      width: '24.38rem',
      disableClose: true,
      autoFocus: false,
      data: { ...endOutboundScan, index },
    });
  }

  /**
   * used to delete record from table
   *
   * @param {number} index
   * @memberof OutboundScanTableComponent
   */
  deleteOutboundScan(index: number) {
    const tableData = this.dataSource.data;
    tableData.splice(index, 1);
    this.dataSource.data = [...tableData];
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof EndOutboundScanTableComponent
   */
  get isFilterEnabled() {
    return (
      this.movementData.formControl.value !== '' ||
      this.departDate.formControl.value !== ''
    );
  }

  /**
   * Used to get stoppable movements
   *
   * @private
   * @memberof EndOutboundScanTableComponent
   */
  private getStoppableMovements() {
    return this._endOutboundScanGleService
      .getStoppableMovements()
      .pipe(
        take(1),
        map((movementsResponse) => {
          const mvmts = movementsResponse?.response?.mvmts || [];
          if (mvmts.length) {
            this.tableData = mvmts.map((movement) => {
              return {
                movement: movement.mvmtNumber,
                ...movement,
                departDate: formatDate(
                  movement.departDate,
                  'MMM dd, yyyy',
                  'en-US',
                ),
              };
            });
            this.dataSource.data = this.tableData as EndOutboundScan[];
            return;
          }

          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }),
      )
      .subscribe();
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: EndOutboundScan, filter: string) => boolean}
   * @memberof EndOutboundScanTableComponent
   */
  private createFilter(): (data: EndOutboundScan, filter: string) => boolean {
    return (data: EndOutboundScan, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof EndOutboundScanTableComponent
   */
  private filterByColumn(
    filterKey: string,
    data: EndOutboundScan,
    filters: EndOutboundScan,
  ) {
    switch (filterKey) {
      case ColumnIds.DEPART_DATE:
        if (filters.departDate !== '') {
          const filteredStartDate = new Date(filters.departDate);
          const dataDate = new Date(data.departDate);
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      default:
        return this.setDefaults(filters, data, filterKey);
    }
  }

  /**
   * return default values
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof OutboundScanTableComponent
   */
  private setDefaults(
    filters: EndOutboundScan,
    data: EndOutboundScan,
    filterKey: string,
  ) {
    const value = filters[filterKey as keyof EndOutboundScan];
    const rowValue = data[filterKey as keyof EndOutboundScan];
    const searchData = rowValue?.toString().toLowerCase() || '';
    const searchValue = value?.toString().toLowerCase();
    return searchData.includes(searchValue);
  }
}
